import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { Tag } from 'antd';

import {
  Button,
  FieldInfoGroup,
  SectionBox,
  UpdatePersonalInformation,
} from '@/components';

import { EmailStatusEnum } from '@/api';
import {
  modalSelectors,
  profileSelectors,
  userSelectors,
} from '@/redux/selectors';
import { personalInfoModalActions } from '@/redux/actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { AuthProviderEnum } from '@/config';

import styles from './PersonalInformation.module.sass';

interface PersonalInformationProps {}

const PersonalInformation: FC<PersonalInformationProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const { data: profileData, loading: profileDataLoading } = useAppSelector(
    profileSelectors.getProfileSlice,
  );

  const personalInfoModalSlice = useAppSelector(
    modalSelectors.getPersonalInfoModalSlice,
  );

  const showEditProfileButton =
    AuthProviderEnum.GOOGLE === userData?.provider ||
    AuthProviderEnum.DEFAULT === userData?.provider;

  const sectionTitle = () => {
    const title = t('translation.user_personal_info_title');

    if (!userData?.discount) {
      return title;
    }
    return (
      <div className={styles.sectionTitle}>
        <div>{title}</div>
        <Tag>
          {parse(
            t('translation.current_discount_msg', {
              value: `${userData?.discount || 0}%`,
            }),
          )}
        </Tag>
      </div>
    );
  };

  const fieldGroupActions = () => {
    return (
      showEditProfileButton && (
        <Button
          className={styles.actionButton}
          type="secondary"
          corner="rect"
          disabled={profileDataLoading}
          onClick={() => dispatch(personalInfoModalActions.openModal())}
        >
          {t('translation.edit_profile_link')}
        </Button>
      )
    );
  };

  return (
    <>
      <SectionBox>
        {{
          body: (
            <FieldInfoGroup
              title={sectionTitle()}
              actions={fieldGroupActions()}
            >
              <table className={styles.infoTable}>
                <tbody>
                  <tr>
                    <td>{t('translation.user_fullname_label')}:</td>
                    <td>
                      <b>{userData?.fullName}</b>
                    </td>
                  </tr>

                  {showEditProfileButton && (
                    <tr>
                      <td>{t('translation.user_email_label')}:</td>
                      <td>
                        <b>{userData?.emailAddress}</b>
                        {userData?.emailStatus ===
                          EmailStatusEnum.UNCONFIRMED && (
                          <>
                            {' '}
                            <Tag color={styles.warning_color}>
                              {t('translation.email_unverified')}
                            </Tag>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </FieldInfoGroup>
          ),
        }}
      </SectionBox>

      <UpdatePersonalInformation
        onClose={() => dispatch(personalInfoModalActions.closeModal())}
        isOpen={personalInfoModalSlice.isOpen}
      />
    </>
  );
};

export default PersonalInformation;
